import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';


interface FormData {
  organisation: string;
  industry: string;
  revenue: string;
  fullName: string;
  titleRole: string;
  isDecisionMaker: string;
  decisionMakerName: string;
  email: string;
  aiSituation: string;
  mandate: string;
  timeline: string;
  source: string;
}

const initialFormData: FormData = {
  organisation: '',
  industry: '',
  revenue: '',
  fullName: '',
  titleRole: '',
  isDecisionMaker: '',
  decisionMakerName: '',
  email: '',
  aiSituation: '',
  mandate: '',
  timeline: '',
  source: '',
};

export const ApplyPage = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Apply for Advisory Access — Custom Group';
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        name: formData.fullName,
        email: formData.email,
        company: formData.organisation,
        role: formData.titleRole,
        industry: formData.industry,
        revenue: formData.revenue,
        isDecisionMaker: formData.isDecisionMaker,
        decisionMakerName: formData.decisionMakerName,
        aiSituation: formData.aiSituation,
        mandate: formData.mandate,
        timeline: formData.timeline,
        referralSource: formData.source,
        source: 'apply-page',
      };

      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Submission failed. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full bg-[#111111] border border-[#222222] text-[#f5f5f5] px-4 py-3 text-sm focus:outline-none focus:border-[#E8651A] transition-colors placeholder-[#444444]';
  const labelClass = 'block text-xs text-[#888888] uppercase tracking-widest mb-2';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      {/* Nav */}
      <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-lg font-semibold tracking-tight text-[#f5f5f5]">
              Custom Group
            </Link>
            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
              <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
              <Link to="/#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</Link>
              <Link to="/#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</Link>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-2 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
              >
                Apply for Access
              </Link>
            </div>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-[#888888] hover:text-[#f5f5f5]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#222222] py-6 flex flex-col gap-4 text-sm font-medium">
              <Link to="/blog" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Insights</Link>
              <Link to="/#approach" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Our Approach</Link>
              <Link to="/#access" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Advisory Access</Link>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-3 text-sm font-semibold text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Apply for Access
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-20">
        {submitted ? (
          <div className="text-center py-20">
            <div className="w-12 h-px bg-[#E8651A] mx-auto mb-8" />
            <h1 className="text-2xl font-bold mb-4">Application Received</h1>
            <p className="text-[#888888] leading-relaxed">
              Your application has been received. We'll be in touch within 2 business days if there's a potential fit.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-12">
              <div className="w-12 h-px bg-[#E8651A] mb-8" />
              <h1 className="text-3xl font-bold mb-4 tracking-tight">Advisory Access Application</h1>
              <p className="text-[#888888] leading-relaxed mb-4">
                We review every application personally. If there is a potential fit, you will hear from us within 2 business days.
              </p>
              <p className="text-xs text-[#555555] leading-relaxed">
                We are not a fit for exploratory or low-budget initiatives. Initial engagements begin at $7,500.
                Advisory retainer hours are available from $10,000 (4-hour block).
              </p>
            </div>

            {error && (
              <div className="border border-red-800 bg-red-900/20 text-red-300 px-4 py-3 text-sm mb-8">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">

              {/* Section: Organisation */}
              <div className="border-t border-[#222222] pt-8">
                <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">Organisation</p>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="organisation" className={labelClass}>
                      Organisation name <span className="text-[#E8651A]">*</span>
                    </label>
                    <input
                      id="organisation"
                      name="organisation"
                      type="text"
                      required
                      value={formData.organisation}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Acme Holdings"
                    />
                  </div>

                  <div>
                    <label htmlFor="industry" className={labelClass}>
                      Industry <span className="text-[#E8651A]">*</span>
                    </label>
                    <input
                      id="industry"
                      name="industry"
                      type="text"
                      required
                      value={formData.industry}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g. Financial Services, Logistics, Manufacturing"
                    />
                  </div>

                  <div>
                    <label htmlFor="revenue" className={labelClass}>
                      Annual revenue <span className="text-[#E8651A]">*</span>
                    </label>
                    <select
                      id="revenue"
                      name="revenue"
                      required
                      value={formData.revenue}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="" disabled>Select range</option>
                      <option value="Under $1M">Under $1M</option>
                      <option value="$1M – $10M">$1M – $10M</option>
                      <option value="$10M – $50M">$10M – $50M</option>
                      <option value="$50M – $250M">$50M – $250M</option>
                      <option value="$250M+">$250M+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Section: You */}
              <div className="border-t border-[#222222] pt-8">
                <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">You</p>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="fullName" className={labelClass}>
                      Full name <span className="text-[#E8651A]">*</span>
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Jane Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="titleRole" className={labelClass}>
                      Title / Role <span className="text-[#E8651A]">*</span>
                    </label>
                    <input
                      id="titleRole"
                      name="titleRole"
                      type="text"
                      required
                      value={formData.titleRole}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Chief Operating Officer"
                    />
                  </div>

                  <div>
                    <label htmlFor="isDecisionMaker" className={labelClass}>
                      Are you the decision-maker for this engagement? <span className="text-[#E8651A]">*</span>
                    </label>
                    <select
                      id="isDecisionMaker"
                      name="isDecisionMaker"
                      required
                      value={formData.isDecisionMaker}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="" disabled>Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No – I report to someone else">No — I report to someone else</option>
                      <option value="Joint decision">Joint decision with another executive</option>
                    </select>
                  </div>

                  {formData.isDecisionMaker && formData.isDecisionMaker !== 'Yes' && (
                    <div>
                      <label htmlFor="decisionMakerName" className={labelClass}>
                        Who is the decision-maker?
                      </label>
                      <input
                        id="decisionMakerName"
                        name="decisionMakerName"
                        type="text"
                        value={formData.decisionMakerName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Name and title"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className={labelClass}>
                      Work email <span className="text-[#E8651A]">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="jane@acmeholdings.com"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Mandate */}
              <div className="border-t border-[#222222] pt-8">
                <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">Mandate</p>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="aiSituation" className={labelClass}>
                      What is your current AI situation? <span className="text-[#E8651A]">*</span>
                    </label>
                    <select
                      id="aiSituation"
                      name="aiSituation"
                      required
                      value={formData.aiSituation}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="" disabled>Select</option>
                      <option value="Exploring – no deployment yet">Exploring — no deployment yet</option>
                      <option value="Have tools, no coherent strategy">Have tools, but no coherent strategy</option>
                      <option value="Failed or stalled deployment">Failed or stalled deployment</option>
                      <option value="Active deployment, want to scale or validate">Active deployment — want to scale or validate ROI</option>
                      <option value="Ready to invest, need direction">Ready to invest — need strategic direction</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mandate" className={labelClass}>
                      Describe the mandate you're bringing <span className="text-[#E8651A]">*</span>
                    </label>
                    <textarea
                      id="mandate"
                      name="mandate"
                      required
                      rows={5}
                      value={formData.mandate}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="What specific problem or opportunity are you trying to address? What does success look like? What have you tried? (3–5 sentences)"
                    />
                  </div>

                  <div>
                    <label htmlFor="timeline" className={labelClass}>
                      Decision timeline <span className="text-[#E8651A]">*</span>
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      required
                      value={formData.timeline}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="" disabled>Select</option>
                      <option value="Under 30 days">Under 30 days</option>
                      <option value="1–3 months">1–3 months</option>
                      <option value="3–6 months">3–6 months</option>
                      <option value="No firm timeline">No firm timeline</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="source" className={labelClass}>
                      How did you find us?
                    </label>
                    <input
                      id="source"
                      name="source"
                      type="text"
                      value={formData.source}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="LinkedIn, referral, article, search…"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4 border-t border-[#222222]">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#E8651A] text-[#0a0a0a] py-4 text-sm font-semibold hover:bg-[#C9520E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </button>
                <p className="text-xs text-[#555555] text-center mt-4">
                  We do not share your information. This is not a sales form. Not every application proceeds.
                </p>
              </div>
            </form>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <p className="text-[#f5f5f5] font-semibold mb-1">Custom Group · Ballito, South Africa</p>
            <p className="text-xs text-[#888888]">© 2026 Custom Group. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
